import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  InputAdornment,
  Alert,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  Popover,
  Tooltip,
  Chip,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import SettingsPageLayout from './SettingsPageLayout';
import { useSettingsForm } from '../SettingsFormContext';
import { enhancedApi } from '../../api';

const ACCEPTED_LOGO_TYPES = ['image/png', 'image/svg+xml', 'image/jpeg', 'image/jpg'];
const ACCEPTED_FAVICON_TYPES = ['image/png', 'image/x-icon', 'image/vnd.microsoft.icon'];
const MAX_LOGO_SIZE = 2 * 1024 * 1024;
const MAX_FAVICON_SIZE = 200 * 1024;

const FONT_OPTIONS = ['Inter', 'Plus Jakarta Sans', 'DM Sans', 'Manrope'];

const toHex = (val) => {
  if (!val || typeof val !== 'string') return '#000000';
  const s = val.startsWith('#') ? val : `#${val}`;
  return /^#[0-9A-Fa-f]{6}$/.test(s) ? s : '#000000';
};

const fromHex = (val) => (val || '#000000').replace(/^#/, '');

// WCAG relative luminance
const sRGBtoLinear = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};
const relativeLuminance = (hex) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return 0.2126 * sRGBtoLinear(r) + 0.7152 * sRGBtoLinear(g) + 0.0722 * sRGBtoLinear(b);
};
const contrastRatio = (hex1, hex2) => {
  const l1 = relativeLuminance(hex1);
  const l2 = relativeLuminance(hex2);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};
const contrastLabel = (ratio) => {
  const r = Math.round(ratio * 10) / 10;
  if (ratio >= 7) return { text: `${r}:1 ✓ AAA`, color: 'success.main' };
  if (ratio >= 4.5) return { text: `${r}:1 ✓ AA`, color: 'success.main' };
  return { text: `${r}:1 ✗ Fail`, color: 'error.main' };
};

function ColorField({ label, value, onChange }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [hexInput, setHexInput] = useState(fromHex(value));

  useEffect(() => {
    setHexInput(fromHex(value));
  }, [value]);

  const handleHexChange = (raw) => {
    setHexInput(raw);
    const h = raw.replace(/^#/, '');
    if (/^[0-9A-Fa-f]{6}$/.test(h)) onChange(`#${h}`);
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: value,
            cursor: 'pointer',
            flexShrink: 0,
          }}
        />
        <TextField
          size="small"
          value={hexInput}
          onChange={(e) => handleHexChange(e.target.value)}
          sx={{ width: 110 }}
          InputProps={{ startAdornment: <InputAdornment position="start">#</InputAdornment> }}
        />
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 1 }}>
          <input
            type="color"
            value={value}
            onChange={(e) => { onChange(e.target.value); setHexInput(fromHex(e.target.value)); }}
            style={{ width: 200, height: 160, border: 'none', cursor: 'pointer', display: 'block' }}
          />
        </Box>
      </Popover>
    </Box>
  );
}

function FileUploadField({ label, hint, accept, maxSize, currentUrl, onFile, onRemove }) {
  const inputRef = useRef(null);
  const [fileError, setFileError] = useState('');

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const acceptedTypes = accept.split(',').map((s) => s.trim());
    if (!acceptedTypes.includes(file.type)) {
      setFileError(`Invalid file type. Accepted: ${accept}`);
      return;
    }
    if (file.size > maxSize) {
      setFileError(`File too large. Max ${Math.round(maxSize / 1024)}KB`);
      return;
    }
    setFileError('');
    onFile(file, URL.createObjectURL(file));
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {hint}
        </Typography>
      )}
      {currentUrl && (
        <Box
          sx={{
            mb: 1,
            width: 120,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          <img src={currentUrl} alt={label} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <input ref={inputRef} type="file" accept={accept} onChange={handleChange} style={{ display: 'none' }} />
        <Button variant="outlined" size="small" onClick={() => inputRef.current?.click()}>
          Upload
        </Button>
        {currentUrl && (
          <Button variant="text" size="small" color="error" onClick={onRemove}>
            Remove
          </Button>
        )}
      </Box>
      {fileError && (
        <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
          {fileError}
        </Typography>
      )}
    </Box>
  );
}

const EMPTY_FORM = {
  logoUrl: '',
  logoFile: null,
  faviconUrl: '',
  faviconFile: null,
  emailHeaderLogoUrl: '',
  emailHeaderLogoFile: null,
  primaryColor: '#1976d2',
  secondaryColor: '#424242',
  accentColor: '#6366F1',
  bgLight: '#FFFFFF',
  bgDark: '#121212',
  headingFont: 'Inter',
  bodyFont: 'Inter',
  buttonRadius: 10,
  cardRadius: 12,
  companyDisplayName: '',
  customDomain: '',
  welcomeMessage: '',
  footerText: '',
  supportEmail: '',
  emailSignatureTemplate: '',
  emailAccentColor: '',
};

const CustomBranding = () => {
  const { setIsDirty } = useSettingsForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savedForm, setSavedForm] = useState(EMPTY_FORM);
  const [previewMode, setPreviewMode] = useState('light');

  const isDirty = JSON.stringify(form) !== JSON.stringify(savedForm);

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const setField = useCallback((key, val) => {
    setForm((prev) => ({ ...prev, [key]: val }));
  }, []);

  const fetchBranding = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await enhancedApi.getBranding();
      const d = res.data?.data ?? res.data ?? {};
      const loaded = {
        logoUrl: d.logo_url || '',
        logoFile: null,
        faviconUrl: d.favicon_url || '',
        faviconFile: null,
        emailHeaderLogoUrl: d.email_header_logo_url || '',
        emailHeaderLogoFile: null,
        primaryColor: toHex(d.primary_color || '#1976d2'),
        secondaryColor: toHex(d.secondary_color || '#424242'),
        accentColor: toHex(d.accent_color || '#6366F1'),
        bgLight: toHex(d.bg_light || '#FFFFFF'),
        bgDark: toHex(d.bg_dark || '#121212'),
        headingFont: d.heading_font || 'Inter',
        bodyFont: d.body_font || 'Inter',
        buttonRadius: d.button_radius ?? 10,
        cardRadius: d.card_radius ?? 12,
        companyDisplayName: d.company_display_name || '',
        customDomain: d.custom_domain || '',
        welcomeMessage: d.welcome_message || '',
        footerText: d.footer_text || '',
        supportEmail: d.support_email || '',
        emailSignatureTemplate: d.email_signature_template || '',
        emailAccentColor: toHex(d.email_accent_color || d.primary_color || '#1976d2'),
      };
      setForm(loaded);
      setSavedForm(loaded);
    } catch {
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const fd = new FormData();
      if (form.logoFile) fd.append('logo', form.logoFile);
      if (form.faviconFile) fd.append('favicon', form.faviconFile);
      if (form.emailHeaderLogoFile) fd.append('email_header_logo', form.emailHeaderLogoFile);
      fd.append('primary_color', form.primaryColor);
      fd.append('secondary_color', form.secondaryColor);
      fd.append('accent_color', form.accentColor);
      fd.append('bg_light', form.bgLight);
      fd.append('bg_dark', form.bgDark);
      fd.append('heading_font', form.headingFont);
      fd.append('body_font', form.bodyFont);
      fd.append('button_radius', String(form.buttonRadius));
      fd.append('card_radius', String(form.cardRadius));
      fd.append('company_display_name', form.companyDisplayName);
      fd.append('custom_domain', form.customDomain);
      fd.append('welcome_message', form.welcomeMessage);
      fd.append('footer_text', form.footerText);
      fd.append('support_email', form.supportEmail);
      fd.append('email_signature_template', form.emailSignatureTemplate);
      fd.append('email_accent_color', form.emailAccentColor);
      await enhancedApi.updateBranding(fd);
      setToast(true);
      setIsDirty(false);
      await fetchBranding();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setForm(savedForm);
  };

  const bgColor = previewMode === 'dark' ? form.bgDark : form.bgLight;
  const contrastInfo = contrastLabel(contrastRatio(form.primaryColor, bgColor));

  if (loading) {
    return (
      <SettingsPageLayout title="Custom Branding" description="Customize logos, colors, and branding for the client portal." noCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </SettingsPageLayout>
    );
  }

  return (
    <SettingsPageLayout title="Custom Branding" description="Customize logos, colors, and branding for the client portal." maxWidth={1100} noCard>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} alignItems="flex-start">
        {/* Left column — controls */}
        <Grid item xs={12} lg={7}>
          {/* Brand Identity */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Brand Identity</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <FileUploadField
                  label="Logo"
                  hint="PNG, SVG or JPG · max 2MB"
                  accept="image/png,image/svg+xml,image/jpeg"
                  maxSize={MAX_LOGO_SIZE}
                  currentUrl={form.logoUrl}
                  onFile={(file, preview) => { setField('logoFile', file); setField('logoUrl', preview); }}
                  onRemove={() => { setField('logoFile', null); setField('logoUrl', ''); }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FileUploadField
                  label="Favicon"
                  hint="PNG or ICO · 32×32 · max 200KB"
                  accept="image/png,image/x-icon"
                  maxSize={MAX_FAVICON_SIZE}
                  currentUrl={form.faviconUrl}
                  onFile={(file, preview) => { setField('faviconFile', file); setField('faviconUrl', preview); }}
                  onRemove={() => { setField('faviconFile', null); setField('faviconUrl', ''); }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FileUploadField
                  label="Email Header Logo"
                  hint="PNG, SVG or JPG · max 2MB"
                  accept="image/png,image/svg+xml,image/jpeg"
                  maxSize={MAX_LOGO_SIZE}
                  currentUrl={form.emailHeaderLogoUrl}
                  onFile={(file, preview) => { setField('emailHeaderLogoFile', file); setField('emailHeaderLogoUrl', preview); }}
                  onRemove={() => { setField('emailHeaderLogoFile', null); setField('emailHeaderLogoUrl', ''); }}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Colors */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Colors</Typography>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <ColorField label="Primary" value={form.primaryColor} onChange={(v) => setField('primaryColor', v)} />
                <Typography variant="caption" sx={{ color: contrastInfo.color, mt: 0.5, display: 'block' }}>
                  vs. Background: {contrastInfo.text}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorField label="Secondary" value={form.secondaryColor} onChange={(v) => setField('secondaryColor', v)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorField label="Accent" value={form.accentColor} onChange={(v) => setField('accentColor', v)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorField label="Background (light)" value={form.bgLight} onChange={(v) => setField('bgLight', v)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <ColorField label="Background (dark)" value={form.bgDark} onChange={(v) => setField('bgDark', v)} />
              </Grid>
            </Grid>
          </Paper>

          {/* Typography */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Typography</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Heading font</InputLabel>
                  <Select value={form.headingFont} label="Heading font" onChange={(e) => setField('headingFont', e.target.value)}>
                    {FONT_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Body font</InputLabel>
                  <Select value={form.bodyFont} label="Body font" onChange={(e) => setField('bodyFont', e.target.value)}>
                    {FONT_OPTIONS.map((f) => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>

          {/* Shapes */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Shapes</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Button radius — {form.buttonRadius}px
                </Typography>
                <Slider min={0} max={16} value={form.buttonRadius} onChange={(_, v) => setField('buttonRadius', v)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Card radius — {form.cardRadius}px
                </Typography>
                <Slider min={0} max={20} value={form.cardRadius} onChange={(_, v) => setField('cardRadius', v)} />
              </Grid>
            </Grid>
          </Paper>

          {/* Client Portal */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Client Portal</Typography>
            <TextField
              fullWidth
              label="Company display name"
              value={form.companyDisplayName}
              onChange={(e) => setField('companyDisplayName', e.target.value.slice(0, 100))}
              inputProps={{ maxLength: 100 }}
              helperText={`${form.companyDisplayName.length}/100`}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Custom domain"
              value={form.customDomain}
              onChange={(e) => setField('customDomain', e.target.value)}
              placeholder="portal.yourfirm.com"
              helperText="Shown in the client portal address bar"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Welcome message"
              value={form.welcomeMessage}
              onChange={(e) => setField('welcomeMessage', e.target.value)}
              helperText="Displayed on the portal login screen"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Footer text"
              value={form.footerText}
              onChange={(e) => setField('footerText', e.target.value)}
              helperText="Copyright line, links, legal text"
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="email"
              label="Support email"
              value={form.supportEmail}
              onChange={(e) => setField('supportEmail', e.target.value)}
              helperText="Shown in portal footer and contact links"
            />
          </Paper>

          {/* Email Branding */}
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2.5, fontWeight: 700 }}>Email Branding</Typography>
            <Box sx={{ mb: 2 }}>
              <ColorField label="Email accent color" value={form.emailAccentColor} onChange={(v) => setField('emailAccentColor', v)} />
            </Box>
            <TextField
              fullWidth
              multiline
              rows={5}
              label="Email signature template"
              value={form.emailSignatureTemplate}
              onChange={(e) => setField('emailSignatureTemplate', e.target.value)}
              helperText="Available tags: {{user.name}}, {{firm.name}}, {{user.email}}, {{firm.phone}}"
            />
          </Paper>
        </Grid>

        {/* Right column — live preview */}
        <Grid item xs={12} lg={5}>
          <Box sx={{ position: 'sticky', top: 16 }}>
            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>Live Preview</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {['light', 'dark'].map((m) => (
                    <Chip
                      key={m}
                      label={m.charAt(0).toUpperCase() + m.slice(1)}
                      size="small"
                      variant={previewMode === m ? 'filled' : 'outlined'}
                      onClick={() => setPreviewMode(m)}
                      sx={{ textTransform: 'capitalize', cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              </Box>

              {/* Portal mockup */}
              <Box
                sx={{
                  borderRadius: `${form.cardRadius}px`,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: bgColor,
                }}
              >
                {/* Header bar */}
                <Box
                  sx={{
                    height: 52,
                    px: 2,
                    display: 'flex',
                    alignItems: 'center',
                    bgcolor: form.primaryColor,
                  }}
                >
                  {form.logoUrl ? (
                    <img src={form.logoUrl} alt="" style={{ maxHeight: 32, maxWidth: 120, objectFit: 'contain' }} />
                  ) : (
                    <Typography
                      sx={{
                        fontFamily: `'${form.headingFont}', sans-serif`,
                        fontWeight: 700,
                        color: 'rgba(255,255,255,0.95)',
                        fontSize: 15,
                      }}
                    >
                      {form.companyDisplayName || 'Client Portal'}
                    </Typography>
                  )}
                </Box>

                {/* Sample content */}
                <Box sx={{ p: 2.5 }}>
                  {form.welcomeMessage && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: `'${form.bodyFont}', sans-serif`,
                        color: previewMode === 'dark' ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                        mb: 2,
                        fontStyle: 'italic',
                      }}
                    >
                      {form.welcomeMessage}
                    </Typography>
                  )}

                  {/* Sample card */}
                  <Box
                    sx={{
                      borderRadius: `${form.cardRadius}px`,
                      border: '1px solid',
                      borderColor: previewMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'divider',
                      p: 2,
                      mb: 2,
                      bgcolor: previewMode === 'dark' ? 'rgba(255,255,255,0.05)' : 'background.paper',
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontFamily: `'${form.headingFont}', sans-serif`,
                        color: previewMode === 'dark' ? 'rgba(255,255,255,0.9)' : 'text.primary',
                        mb: 1,
                      }}
                    >
                      Sample Document
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontFamily: `'${form.bodyFont}', sans-serif`,
                        color: previewMode === 'dark' ? 'rgba(255,255,255,0.5)' : 'text.secondary',
                      }}
                    >
                      Tax Return 2024 · Pending signature
                    </Typography>
                  </Box>

                  {/* Sample button */}
                  <Box
                    component="button"
                    sx={{
                      borderRadius: `${form.buttonRadius}px`,
                      bgcolor: form.primaryColor,
                      color: '#ffffff',
                      border: 'none',
                      px: 2.5,
                      py: 1,
                      fontFamily: `'${form.bodyFont}', sans-serif`,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'default',
                      mr: 1.5,
                    }}
                  >
                    Sign Document
                  </Box>
                  <Box
                    component="button"
                    sx={{
                      borderRadius: `${form.buttonRadius}px`,
                      bgcolor: 'transparent',
                      color: form.accentColor,
                      border: `1.5px solid ${form.accentColor}`,
                      px: 2.5,
                      py: 1,
                      fontFamily: `'${form.bodyFont}', sans-serif`,
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: 'default',
                    }}
                  >
                    Cancel
                  </Box>
                </Box>

                {/* Footer */}
                {(form.footerText || form.supportEmail) && (
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      borderTop: '1px solid',
                      borderColor: previewMode === 'dark' ? 'rgba(255,255,255,0.08)' : 'divider',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: previewMode === 'dark' ? 'rgba(255,255,255,0.4)' : 'text.disabled',
                        fontFamily: `'${form.bodyFont}', sans-serif`,
                      }}
                    >
                      {form.footerText || form.supportEmail}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>
        </Grid>
      </Grid>

      {/* Sticky save bar */}
      {isDirty && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            You have unsaved changes
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button variant="outlined" size="small" onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" size="small" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      )}

      <Snackbar
        open={toast}
        autoHideDuration={4000}
        onClose={() => setToast(false)}
        message="Branding updated · changes will reflect across portal in ~30 seconds"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </SettingsPageLayout>
  );
};

export default CustomBranding;
